import { createFrame, type FrameType } from "@/api/spaces";
import { getFrameDefaultSize } from "@/lib/frame-defaults";
import { useFramesStore } from "@/stores/frames-store";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { applyFramePatch, ingestFrame } from "@/lib/frame-ingest";

/**
 * Ensures a singleton-type frame (`taskboard`, `memory`) exists for the
 * project and is in the frames store, returning its id.
 *
 * The backend find-or-creates singleton frame types per project under a
 * row lock (`apps/spaces/services.py`), so calling `createFrame` here is
 * always safe — it returns the existing row instead of duplicating one.
 * That makes a pre-create existence check unnecessary (and a needless
 * extra failure point), so this never calls `listFrames`.
 */
export async function ensureSingletonFrame(
  projectId: string,
  frameType: FrameType,
  title: string,
  signal?: AbortSignal,
): Promise<string> {
  signal?.throwIfAborted();
  const size = getFrameDefaultSize(frameType);
  const frame = await createFrame(projectId, {
    frame_type: frameType,
    title,
    content: {},
    width: size.w,
    height: size.h,
  });

  // Creation is idempotent server-side. A cancelled caller must not mount an
  // old project's response into the currently visible workspace.
  signal?.throwIfAborted();

  if (useFramesStore.getState().framesById[frame.id]) {
    applyFramePatch(frame.id, frame, { origin: "server" });
  } else {
    // The onboarding caller settles all of its frames at once.
    workspaceViewAdapter().prepareFrameArrival(frame.id);
    ingestFrame(frame);
  }

  return frame.id;
}
